import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./GuestCheckoutInformationDialog.module.scss";
import Input from "../../../../common/components/new/ui/Input";
import { CityIcon, HomeIcon, InfoIcon, LetterIcon, MailboxIcon, ProfileIcon } from "../../../../assets/icons";
import PhoneNumberInput from "../../../../common/components/new/phone-number/PhoneNumberInput";
import Select, { SelectItem } from "../../../../common/components/new/select/Select";
import { State } from "../../../../model/state";
import { useLazyGetStatesQuery } from "../../../../service/state-service";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import { useDispatch } from "react-redux";
import { addDeliveryAddress } from "../../../../stores/slices/guest-delivery-address-slice";
import { DeliveryAddress } from "../../../../model/delivery-address";
import { ApplicationRoutes } from "../../../../routes";

type Props = {
	onCloseDialog: () => void;
};
interface FormType {
	firstName: string;
	lastName: string;
	email: string;
	address: string;
	phoneNumber: string;
	city: string;
	state?: SelectItem;
	zip: number;
	additionalInformation: string;
}

const GuestCheckoutInformationDialog: React.FC<Props> = (props: Props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { onCloseDialog } = props;

	const [states, setStates] = useState<State[]>();

	const [getStates] = useLazyGetStatesQuery();

	const fetchStates = useCallback(async () => {
		try {
			let response = await getStates().unwrap();
			setStates(response);
		} catch (error) {
			toast.error("Error loading States");
		}
	}, [getStates]);

	useEffect(() => {
		fetchStates();
	}, []);

	const stateItems = states?.map(state => new SelectItem(state.id, state.name)) || [];

	const {
		register,
		handleSubmit,
		control,
		watch,
		formState: { errors },
	} = useForm<FormType>({
		reValidateMode: "onChange",
		mode: "onChange",
	});

	const registeredInputs = useMemo(() => {
		return {
			firstName: register("firstName", { required: "First name is required" }),
			lastName: register("lastName", { required: "Last name is required" }),
			email: register("email", {
				required: "Email is required",
				pattern: {
					value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
					message: "Invalid email address",
				},
			}),
			address: register("address", { required: "Address is required" }),
			phoneNumber: register("phoneNumber", {
				required: "Phone number is required",
				pattern: {
					value: /^\+(?:[0-9] ?){12}[0-9]$/,
					message: "Invalid phone number",
				},
			}),
			city: register("city", { required: "City is required" }),
			zip: register("zip", { required: "Zip Code is required", valueAsNumber: true }),
			additionalInformation: register("additionalInformation"),
		};
	}, [register, watch]);

	const onSubmit = (data: FormType) => {
		const address: DeliveryAddress = {
			id: "",
			deliveryAddressInformation: {
				firstName: data?.firstName?.trim(),
				lastName: data?.lastName?.trim(),
				phoneNumber: data?.phoneNumber,
				email: data?.email?.trim(),
				additionalInformation: data?.additionalInformation?.trim(),
				address: data?.address?.trim(),
				city: data?.city?.trim(),
				region: data?.state?.label ?? "",
				additionalPhoneNumber: "",
			},
			isDefault: false,
		};
		dispatch(addDeliveryAddress(address));
		navigate(`/${ApplicationRoutes.BILLING_INFO}`);
	};

	return (
		<CommonDialog onCloseDialog={onCloseDialog} noPadding>
			<div className={styles.container}>
				<div className={styles.banner}>
					<svg width="712" height="316" viewBox="0 0 712 316" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M287.722 292.514L198.001 -134.238C169.608 -123.684 142.578 -110.606 117.138 -95.0045L287.949 292.285L287.722 292.514Z"
							fill="black"
							fillOpacity="0.02"
						/>
						<path d="M504.88 -137.682L414.25 292.513L587.333 -100.054C561.438 -115.197 533.727 -127.816 504.88 -137.682Z" fill="black" fillOpacity="0.02" />
						<path d="M372.443 283.108L463.3 -149.84C433.999 -156.952 403.561 -161.082 372.443 -162V283.108Z" fill="black" fillOpacity="0.02" />
						<path d="M329.526 -161.539C298.407 -159.703 268.197 -154.885 239.123 -147.084L329.526 283.11V-161.539Z" fill="black" fillOpacity="0.02" />
						<path d="M185.291 367.541L-73.4248 132.139C-82.5105 155.083 -89.779 179.174 -95.2305 203.723L185.291 367.541Z" fill="black" fillOpacity="0.02" />
						<path
							d="M164.386 405.169L-102.961 249.151C-105.005 267.277 -106.368 285.632 -106.368 304.446V316.147L164.386 404.939V405.169Z"
							fill="black"
							fillOpacity="0.02"
						/>
						<path d="M-11.4297 21.7804C-27.784 43.3475 -42.3211 66.2912 -54.814 90.8409L214.123 335.421L-11.4297 21.7804Z" fill="black" fillOpacity="0.02" />
						<path
							d="M516.684 367.541L806.517 198.446C800.612 173.208 793.116 148.429 783.349 125.026L516.684 367.771V367.541Z"
							fill="black"
							fillOpacity="0.02"
						/>
						<path
							d="M248.427 309.953L81.023 -70.4538C57.8544 -53.0166 36.503 -33.7439 16.9688 -12.4063L248.654 309.723L248.427 309.953Z"
							fill="black"
							fillOpacity="0.02"
						/>
						<path d="M453.555 309.952L689.783 -18.8307C669.568 -40.1683 647.535 -59.2116 623.685 -76.4194L453.555 309.952Z" fill="black" fillOpacity="0.02" />
						<path d="M487.84 335.189L764.046 83.9556C750.871 59.4058 735.653 36.2327 718.617 14.4362L487.84 335.189Z" fill="black" fillOpacity="0.02" />
						<path
							d="M818.996 312.707V304.447C818.996 283.798 817.178 263.378 814.68 243.417L537.566 404.941L818.996 312.478V312.707Z"
							fill="black"
							fillOpacity="0.02"
						/>
					</svg>
				</div>
				<div className={styles.content}>
					<div className={styles.titleContainer}>
						<div className={styles.title}>Guest Checkout Information</div>
						<div className={styles.description}>Manage your Checkout Information</div>
					</div>
					<div className={styles.content1}>
						<div className={styles.props}>
							<div className={styles.inputGroup}>
								<Input
									ref={registeredInputs.firstName.ref}
									label="First Name"
									placeholder="Name 1"
									error={errors.firstName?.message}
									inputProps={registeredInputs.firstName}
								>
									<ProfileIcon />
								</Input>
								<Input
									ref={registeredInputs.lastName.ref}
									label="Last Name"
									placeholder="Name 2"
									error={errors.lastName?.message}
									inputProps={registeredInputs.lastName}
								>
									<ProfileIcon />
								</Input>
							</div>
							<Input
								ref={registeredInputs.email.ref}
								label="Email"
								placeholder="name@sample.com"
								error={errors.email?.message}
								inputProps={registeredInputs.email}
							>
								<LetterIcon />
							</Input>
							<Input
								ref={registeredInputs.address.ref}
								label="Address"
								placeholder="42 Olumide Street, Lagos"
								error={errors.address?.message}
								inputProps={registeredInputs.address}
							>
								<HomeIcon />
							</Input>
							<PhoneNumberInput ref={registeredInputs.phoneNumber.ref} error={errors.phoneNumber?.message} inputProps={registeredInputs.phoneNumber} />
							<Input ref={registeredInputs.city.ref} label="City" placeholder="Surulere" error={errors.city?.message} inputProps={registeredInputs.city}>
								<CityIcon />
							</Input>
							<div className={styles.inputGroup}>
								<Controller
									name="state"
									control={control}
									rules={{
										required: "State is required",
									}}
									render={({ field, fieldState }) => (
										<Select
											label="State"
											items={stateItems}
											selected={field.value}
											placeholder="Select State"
											error={fieldState.error?.message}
											searchable={true}
											onChange={selectedItem => {
												field.onChange(selectedItem);
											}}
										>
											<ProfileIcon />
										</Select>
									)}
								/>
								<Input ref={registeredInputs.zip.ref} label="Zip" placeholder="100011" error={errors.zip?.message} inputProps={registeredInputs.zip}>
									<MailboxIcon />
								</Input>
							</div>
							<Input
								ref={registeredInputs.additionalInformation.ref}
								label="Additional Information"
								placeholder="Close to 29 Adejumo Avenue, Lagos"
								error={errors.additionalInformation?.message}
								inputProps={registeredInputs.additionalInformation}
							>
								<InfoIcon />
							</Input>
						</div>
						<div className={styles.controls}>
							<button className={`standardButton ${styles.control} ${styles.back}`} onClick={onCloseDialog}>
								<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M4.79287 9L1.14645 5.35355C0.951184 5.1583 0.951184 4.8417 1.14644 4.64645L4.79287 1"
										stroke="#FF7926"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								Back
							</button>
							<button onClick={handleSubmit(onSubmit)} className={`standardButton ${styles.control} ${styles.continue}`}>
								Continue
								<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M1.20713 9L4.85355 5.35355C5.04882 5.1583 5.04882 4.8417 4.85356 4.64645L1.20713 1"
										stroke="#FF7926"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</CommonDialog>
	);
};

export default GuestCheckoutInformationDialog;
